'use client';

import { useCallback, useEffect, useState } from 'react';

type UseIntersectionObserverOptions = {
    root?: Element | null;
    rootMargin?: string;
    threshold?: number;
};

type ObserverEntry = {
    observer: IntersectionObserver;
    callbacks: Map<Element, (isIntersecting: boolean) => void>;
};

const sharedObservers = new Map<string, ObserverEntry>();

const getOptionsKey = (root: Element | null, rootMargin: string, threshold: number): string | null => {
    if (root !== null) {
        return null;
    }
    return `${rootMargin}|${threshold}`;
};

const getOrCreateObserver = (
    root: Element | null,
    rootMargin: string,
    threshold: number
): { entry: ObserverEntry; key: string } | null => {
    if (!window.IntersectionObserver) {
        return null;
    }
    const key = getOptionsKey(root, rootMargin, threshold);
    if (key === null) {
        return null;
    }
    let entry = sharedObservers.get(key);
    if (!entry) {
        const callbacks = new Map<Element, (isIntersecting: boolean) => void>();
        const observer = new window.IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    callbacks.get(e.target)?.(e.isIntersecting);
                }
            },
            { root, rootMargin, threshold }
        );
        entry = { observer, callbacks };
        sharedObservers.set(key, entry);
    }
    return { entry, key };
};

const cleanupObserver = (key: string, element: Element): void => {
    const entry = sharedObservers.get(key);
    if (!entry) return;
    entry.callbacks.delete(element);
    entry.observer.unobserve(element);
    if (entry.callbacks.size === 0) {
        entry.observer.disconnect();
        sharedObservers.delete(key);
    }
};

/** Observes element visibility in the viewport. Returns [refCallback, isIntersecting].
 * Uses a shared IntersectionObserver per options (viewport root) to avoid N observers for N elements. */
export const useIntersectionObserver = <T extends HTMLElement = HTMLDivElement>(
    options: UseIntersectionObserverOptions = {}
): [(node: T | null) => void, boolean] => {
    const { root = null, rootMargin = '100px', threshold = 0 } = options;
    const [element, setElement] = useState<T | null>(null);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const refCallback = useCallback((node: T | null) => setElement(node), []);

    useEffect(() => {
        if (!element || !window.IntersectionObserver) {
            return undefined;
        }
        const result = getOrCreateObserver(root, rootMargin, threshold);
        if (result) {
            const { entry, key } = result;
            entry.callbacks.set(element, setIsIntersecting);
            entry.observer.observe(element);
            return () => cleanupObserver(key, element);
        }
        const observer = new window.IntersectionObserver(([e]) => setIsIntersecting(e.isIntersecting), {
            root,
            rootMargin,
            threshold
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [element, root, rootMargin, threshold]);

    return [refCallback, isIntersecting];
};
